import { useCallback, useEffect, useMemo, useState } from "preact/hooks"
import useSWR from "swr"
import { Link } from "wouter-preact"
import { format, parseISO } from "date-fns"
import { Loader2, Wand2 } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Label } from "../../components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select"
import { SearchableSelect } from "@/components/searchable_select"
import { useSearchParams } from "@/utils"
import {
  getRetailerMappingCandidates,
  listDistributorReports,
  listRetailerAliases,
  suggestRetailerAliasApply,
  suggestRetailerAliasPreview,
  upsertRetailerAlias,
  deleteRetailerAlias,
} from "../sales_forecasting/api/distributor_sales_api"
import {
  DistributorSalesReport,
  RetailerAliasRowJson,
  RetailerAliasSuggestPreviewResponse,
  RetailerMappingCandidatesResponse,
  SalesHierarchyPath,
} from "../sales_forecasting/types/distributor_sales"

export function RetailerMappingsPage() {
  const params = useSearchParams<{ report_id?: string }>()
  const [selected_report_id, set_selected_report_id] = useState(params.report_id ?? "")

  useEffect(() => {
    if (params.report_id) {
      set_selected_report_id(params.report_id)
      set_preview(null)
    }
  }, [params.report_id])
  const [deduction_pick, set_deduction_pick] = useState("")
  const [sales_pick, set_sales_pick] = useState("")
  const [scope, set_scope] = useState<"org" | "report">("report")
  const [preview, set_preview] = useState<RetailerAliasSuggestPreviewResponse | null>(null)
  const [preview_loading, set_preview_loading] = useState(false)
  const [apply_loading, set_apply_loading] = useState(false)
  const [error, set_error] = useState<string | null>(null)

  const { data: reports = [] } = useSWR("distributor-reports-for-mappings", () =>
    listDistributorReports({ limit: 200 }).then((r: DistributorSalesReport[]) => r.filter((x) => x.source !== "spins"))
  )

  const effective_report_id = selected_report_id || params.report_id || ""

  const { data: candidates, isLoading: candidates_loading } = useSWR(
    effective_report_id ? ["retailer-mapping-candidates", effective_report_id] : null,
    () => getRetailerMappingCandidates(effective_report_id)
  )

  const deduction_options = useMemo(
    () =>
      (candidates?.deduction_retailers ?? []).map((n: string) => ({
        value: n,
        label: n,
      })),
    [candidates]
  )

  const sales_options = useMemo(
    () =>
      (candidates?.sales_retailers ?? []).map((n: string) => ({
        value: n,
        label: n,
      })),
    [candidates]
  )

  const { data: aliases = [], mutate: mutate_aliases } = useSWR(
    effective_report_id ? ["retailer-aliases", effective_report_id] : null,
    () => listRetailerAliases(effective_report_id)
  )

  const selected_report = useMemo(
    () => reports.find((r: DistributorSalesReport) => r.id === effective_report_id),
    [reports, effective_report_id]
  )

  const on_preview = useCallback(async () => {
    if (!effective_report_id) {
      set_error("Select a report first.")
      return
    }
    set_error(null)
    set_preview_loading(true)
    try {
      const res = await suggestRetailerAliasPreview(effective_report_id)
      set_preview(res)
    } catch (e: unknown) {
      set_error(e instanceof Error ? e.message : "Preview failed")
    } finally {
      set_preview_loading(false)
    }
  }, [effective_report_id])

  const on_apply_preview = useCallback(async () => {
    if (!effective_report_id || !preview?.valid.length) return
    set_error(null)
    set_apply_loading(true)
    try {
      await suggestRetailerAliasApply(effective_report_id, preview.valid)
      set_preview(null)
      await mutate_aliases()
    } catch (e: unknown) {
      set_error(e instanceof Error ? e.message : "Apply failed")
    } finally {
      set_apply_loading(false)
    }
  }, [effective_report_id, preview, mutate_aliases])

  const on_save_manual = useCallback(async () => {
    if (!effective_report_id || !selected_report) {
      set_error("Select a report first.")
      return
    }
    if (!deduction_pick.trim() || !sales_pick.trim()) {
      set_error("Choose both a retailer name from deductions and one from sales data.")
      return
    }
    set_error(null)
    try {
      await upsertRetailerAlias({
        source: selected_report.source,
        report_id: scope === "report" ? effective_report_id : null,
        deduction_retailer_alias: deduction_pick.trim(),
        sales_retailer_name: sales_pick.trim(),
        notes: null,
      })
      set_deduction_pick("")
      set_sales_pick("")
      await mutate_aliases()
    } catch (e: unknown) {
      set_error(e instanceof Error ? e.message : "Save failed")
    }
  }, [effective_report_id, selected_report, scope, deduction_pick, sales_pick, mutate_aliases])

  const on_delete = useCallback(
    async (id: string) => {
      if (!confirm("Delete this mapping?")) return
      set_error(null)
      try {
        await deleteRetailerAlias(id)
        await mutate_aliases()
      } catch (e: unknown) {
        set_error(e instanceof Error ? e.message : "Delete failed")
      }
    },
    [mutate_aliases]
  )

  return (
    <div class="space-y-6 px-6 max-w-5xl">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-semibold text-gray-900">Retailer name mappings</h1>
          <p class="mt-1 text-sm text-gray-600">
            Map retailer names from deductions to the retailer names shown in your sales data so profitability lines up.
            Org-wide mappings apply to every report for that distributor; report-specific ones override for this upload only.
          </p>
        </div>
        <Link href="/sales-analysis">
          <Button variant="outline" size="sm" type="button">
            Back to Sales Analysis
          </Button>
        </Link>
      </div>

      {error ? <p class="text-sm text-red-600">{error}</p> : null}

      <div class="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
        <div class="max-w-md space-y-2">
          <Label>Table report</Label>
          <Select
            value={effective_report_id || undefined}
            onValueChange={(v: string) => {
              set_selected_report_id(v)
              set_preview(null)
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose a report…" />
            </SelectTrigger>
            <SelectContent>
              {reports.map((r: DistributorSalesReport) => {
                const dateStr = format(parseISO(r.report_date), "MMM d, yyyy")
                return (
                  <SelectItem key={r.id} value={r.id}>
                    {r.source.toUpperCase()} — {dateStr}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>

        {effective_report_id ? (
          <>
            <div class="border-t border-gray-100 pt-4 text-sm text-gray-600 space-y-3">
              <p>
                <span class="font-medium text-gray-800">Retailer names from deductions</span> are resolved in the same
                order we use for profitability: <span class="font-medium">split lines</span>, then{" "}
                <span class="font-medium">parsed backup lines</span>, then the{" "}
                <span class="font-medium">deduction header</span>.
              </p>
              {candidates_loading ? (
                <p class="text-gray-500">Loading retailer names…</p>
              ) : candidates ? (
                <CandidateListsBlock candidates={candidates} />
              ) : null}
            </div>

            <div class="space-y-2">
              <p class="text-xs text-gray-600 max-w-3xl leading-relaxed">
                <span class="font-medium text-gray-800">MappingWiz</span> compares deduction retailer names to your sales
                hierarchy (levels 1–3), preferring the most specific store match
                with retailer names from deductions for this report (based on the same dates we use when matching
                deductions), asks the model for suggested pairs, and shows a preview here. Nothing is saved until you
                click <span class="font-medium">Apply</span> or add a mapping manually below. Use{" "}
                <span class="font-medium">Discard preview</span> to run MappingWiz again without applying.
              </p>
              <div class="flex flex-wrap gap-2 items-center">
                <Button
                  type="button"
                  onClick={on_preview}
                  disabled={
                    preview_loading ||
                    apply_loading ||
                    (preview != null && preview.valid.length > 0)
                  }
                >
                  {preview_loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2" />
                  )}
                  MappingWiz
                </Button>
                {preview != null ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => set_preview(null)}>
                    Discard preview
                  </Button>
                ) : null}
              </div>
            </div>

            {preview ? (
              <div
                class={
                  preview.invalid.length > 0 ? "grid gap-4 md:grid-cols-2 text-sm" : "space-y-3 text-sm"
                }
              >
                {preview.invalid.length > 0 ? (
                  <div>
                    <p class="font-medium text-gray-800 mb-2">Issues</p>
                    <ul class="max-h-48 overflow-y-auto space-y-1 text-amber-800">
                      {preview.invalid.map((s: string, i: number) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div>
                  <p class="font-medium text-gray-800 mb-2">Valid suggestions</p>
                  <ul class="max-h-48 overflow-y-auto space-y-1 text-gray-700">
                    {preview.valid.map((p, i: number) => (
                      <li key={i}>
                        <span class="text-gray-500">{p.deduction_retailer}</span> →{" "}
                        <span class="font-medium">
                          {p.sales_hierarchy_display || p.sales_retailer}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {preview.valid.length > 0 ? (
                    <div class="mt-3">
                      <Button type="button" onClick={on_apply_preview} disabled={apply_loading}>
                        {apply_loading ? "Applying…" : `Apply ${preview.valid.length} suggested`}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div class="border-t border-gray-100 pt-4 space-y-3">
              <p class="text-sm font-medium text-gray-800">Add or replace mapping</p>
              <div class="grid gap-3 md:grid-cols-2">
                <div class="space-y-1 md:order-1">
                  <Label>Retailer name from sales data</Label>
                  <SearchableSelect
                    options={sales_options}
                    value={sales_pick}
                    onValueChange={set_sales_pick}
                    placeholder="Search retailer names from sales data…"
                    searchPlaceholder="Search retailer names from sales data…"
                    emptyMessage="No matching retailer names from sales data."
                  />
                </div>
                <div class="space-y-1 md:order-2">
                  <Label>Retailer name from deductions</Label>
                  <SearchableSelect
                    options={deduction_options}
                    value={deduction_pick}
                    onValueChange={set_deduction_pick}
                    placeholder="Search retailer names from deductions…"
                    searchPlaceholder="Search retailer names from deductions…"
                    emptyMessage="No matching retailer names from deductions."
                  />
                </div>
              </div>
              <div class="flex flex-wrap items-center gap-3">
                <Label class="text-sm">Scope</Label>
                <Select value={scope} onValueChange={(v: string) => set_scope(v as "org" | "report")}>
                  <SelectTrigger class="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="report">This report only</SelectItem>
                    <SelectItem value="org">Org-wide ({selected_report?.source.toUpperCase() ?? "source"})</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" onClick={on_save_manual}>
                  Save mapping
                </Button>
              </div>
            </div>

            <div class="border-t border-gray-100 pt-4">
              <p class="text-sm font-medium text-gray-800 mb-2">Current mappings</p>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="text-left border-b border-gray-200">
                      <th class="py-2 pr-2">Sales data name</th>
                      <th class="py-2 pr-2">Deduction name</th>
                      <th class="py-2 pr-2">Source</th>
                      <th class="py-2 pr-2">Scope</th>
                      <th class="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {aliases.map((a: RetailerAliasRowJson) => (
                      <tr key={a.id} class="border-b border-gray-100">
                        <td class="py-2 pr-2">{a.sales_hierarchy_display || a.sales_retailer_name}</td>
                        <td class="py-2 pr-2 font-mono text-xs">{a.deduction_retailer_alias}</td>
                        <td class="py-2 pr-2 text-gray-500">{a.provenance ?? "—"}</td>
                        <td class="py-2 pr-2">{a.scope}</td>
                        <td class="py-2 text-right">
                          <Button type="button" variant="ghost" size="sm" onClick={() => on_delete(a.id)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {aliases.length === 0 ? <p class="text-gray-500 text-sm py-4">No mappings yet.</p> : null}
              </div>
            </div>
          </>
        ) : (
          <p class="text-sm text-gray-500">Select a report to view and edit mappings.</p>
        )}
      </div>
    </div>
  )
}

interface CandidateListsBlockProps {
  candidates: RetailerMappingCandidatesResponse
}

function format_hierarchy_path(path: SalesHierarchyPath): string {
  const parts = [path.level_1, path.level_2, path.level_3].filter(Boolean)
  return parts.join(" → ")
}

function CandidateListsBlock({ candidates }: CandidateListsBlockProps) {
  const hierarchy_lines = useMemo(() => {
    const paths = candidates.sales_hierarchy ?? []
    if (paths.length > 0) {
      return paths.map((p: SalesHierarchyPath) => format_hierarchy_path(p))
    }
    return candidates.sales_retailers.map((n: string) => n)
  }, [candidates])

  return (
    <div class="grid gap-4 md:grid-cols-2">
      <div class="rounded border border-gray-200 bg-gray-50/80 p-3">
        <p class="font-medium text-gray-900 mb-2">
          Sales hierarchy (levels 1–3) — {hierarchy_lines.length} paths, {candidates.sales_retailers.length}{" "}
          mappable names
        </p>
        <ul class="max-h-72 overflow-y-auto font-mono text-xs text-gray-800 space-y-1 pr-1">
          {hierarchy_lines.map((line: string) => (
            <li key={line} class="break-words">
              {line}
            </li>
          ))}
        </ul>
        {hierarchy_lines.length === 0 ? (
          <p class="text-xs text-gray-500 mt-2">No retailer names found on this sales report.</p>
        ) : null}
      </div>
      <div class="rounded border border-gray-200 bg-gray-50/80 p-3">
        <p class="font-medium text-gray-900 mb-2">
          Retailer names from deductions — {candidates.deduction_retailers.length}
        </p>
        <ul class="max-h-72 overflow-y-auto font-mono text-xs text-gray-800 space-y-1 pr-1">
          {candidates.deduction_retailers.map((n: string) => (
            <li key={n} class="break-words">
              {n}
            </li>
          ))}
        </ul>
        {candidates.deduction_retailers.length === 0 ? (
          <p class="text-xs text-gray-500 mt-2">
            No retailer names from deductions for this distributor and report date range.
          </p>
        ) : null}
      </div>
    </div>
  )
}
